import { FC, useEffect, useState } from "react";
import {
    EventName,
    GoalEvent,
    HalfEvent,
    MatchEvent,
    MatchPhone,
} from "refsix-js-models";
import {
    isStoppingClock,
    processEvents,
    showsStoppagesInMatchLog,
} from "refsix-core";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import MatchLogComponent from "../MatchLogComponent";
import {
    computeHalfInfo,
    IndexedMap,
} from "../../services/matchService";
import { ComputedHalfInfo } from "../../models/ComputedHalfInfo";
import { DisplayEvent } from "../../models/DisplayEvent";
import { DisplayEventType } from "../../models/DisplayEventType";
import { RootState, store } from "../../redux/store";
import { calculateMinuteAndAdditional } from "../../services/eventService";
import { formatDurationWithLabel } from "../../utils/formatDuration";
import { formatPeriodEndDisplay } from "../../utils/formatPeriodEndDisplay";
import {
    eventDisplayMinute,
    stoppingClockPeriodEndDisplay,
} from "../../utils/stoppingClockDisplay";

interface MatchEventLogProps {
    match: MatchPhone;
    // Display order of the rows. The results page reads newest-first ("desc");
    // the video replay timeline reads chronologically ("asc"). Defaults to "desc".
    order?: "asc" | "desc";
    // When provided, a tapped row invokes this (e.g. seek the replay video)
    // instead of the default edit/delete popover.
    onEventClick?: (displayEvent: DisplayEvent) => void;
    // Results-page edit/delete handlers, given the tapped display event.
    edit?: (displayEvent: DisplayEvent) => void;
    delete?: (displayEvent: DisplayEvent) => void;
}

/**
 * Renders a match's events as the shared visual log (the same list the results
 * page shows). Extracted from `pages/tabs/fixturesSegments/match.tsx` so it can
 * be reused by the video replay tool, which swaps the row click behaviour for a
 * video seek via `onEventClick`.
 */
const MatchEventLog: FC<MatchEventLogProps> = ({
    match,
    order = "desc",
    onEventClick,
    edit,
    delete: deleteEvent,
}) => {
    const [displayEvents, setDisplayEvents] = useState<DisplayEvent[]>([]);
    const showStoppages = useSelector((state: RootState) =>
        showsStoppagesInMatchLog(
            state.subscriptions.features,
            state.settings.settings.showStoppagesInMatchLog
        )
    );

    useEffect(() => {
        if (match.matchEvents) {
            let eventsArray: MatchEvent[] = match.matchEvents
                ? Object.values(match.matchEvents)
                : [];

            if (!match.eventsProcessed) {
                const processedMatch: MatchPhone = processEvents(match);
                if (processedMatch.matchEvents) {
                    eventsArray = Object.values(processedMatch.matchEvents);
                }
            }

            const periodsAndIntervals = eventsArray.filter((event) => {
                return (
                    event.eventName === EventName.half ||
                    event.eventName === EventName.penalties
                );
            }) as HalfEvent[];

            const playingPeriodInfo: IndexedMap = computeHalfInfo(
                periodsAndIntervals,
                eventsArray
            );

            setDisplayEvents(
                generateDisplayEvents(
                    eventsArray,
                    playingPeriodInfo,
                    match.matchAbandoned === true
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match.matchEvents, showStoppages]);

    function generateMinuteString(
        event: MatchEvent,
        currentHalfInfo: ComputedHalfInfo
    ) {
        const minuteAndAdditionalInfo = calculateMinuteAndAdditional(
            event,
            currentHalfInfo
        );

        return minuteAndAdditionalInfo.additionMinute
            ? `${minuteAndAdditionalInfo.minute}' + ${minuteAndAdditionalInfo.additionMinute}'`
            : `${minuteAndAdditionalInfo.minute}'`;
    }

    function matchAbandoned(events: DisplayEvent[]): DisplayEvent[] {
        const periodEnds = events.filter(
            (e) => e.type === DisplayEventType.periodEnd
        );

        const periodToRemove = periodEnds[periodEnds.length - 1];
        // get the correct end minute from the periodEnd event
        const endMinute = periodToRemove.displayMinute;

        const abandonEvents = events.filter(
            (e) => e.event.eventName === EventName.abandoned
        );

        if (!abandonEvents.length) {
            const message = `Match has matchAbandoned property but no abandoned event ${
                store.getState().auth?.session?.user_id
            }/${match._id}`;
            console.log(message);
            Sentry.captureMessage(message);
            return events;
        }

        // set correct display minute from half end event
        const abandonEvent = abandonEvents[0];
        abandonEvent.displayMinute = endMinute;

        // Remove the half end display event
        const indexToRemove = events.indexOf(periodToRemove);
        events.splice(indexToRemove, 1);
        return events;
    }

    const getHalfTimeScoreForPeriod = (
        period: number
    ): { home: number; away: number } => {
        const scoreCount: { home: number; away: number } = { home: 0, away: 0 };
        if (match.matchEvents) {
            Object.values(match.matchEvents).forEach((event) => {
                if (event.eventName === EventName.goal) {
                    const goalEvent = event as GoalEvent;

                    if (goalEvent.half === period) {
                        if (
                            goalEvent.team.side === "home" ||
                            goalEvent.team.side === "away"
                        ) {
                            scoreCount[goalEvent.team.side]++;
                        }
                    }
                }
            });
        }

        return { ...scoreCount };
    };

    function generateDisplayEvents(
        events: MatchEvent[],
        playingPeriodInfo: IndexedMap,
        abandoned: boolean
    ): DisplayEvent[] {
        if (!playingPeriodInfo) {
            console.log("No playingPeriodInfo");
            return [];
        }

        const output: DisplayEvent[] = [];
        const sortedEvents = events.sort((a, b) => {
            return a.timestamp - b.timestamp;
        });
        // REFSIX-745: a stopping-clock match shows minutes with prior stoppages deducted
        const stoppingClock = isStoppingClock(match);

        let currentPeriod: HalfEvent;
        let currentPeriodInfo: ComputedHalfInfo;

        sortedEvents.forEach((matchEvent) => {
            if (
                matchEvent.eventName === EventName.half ||
                matchEvent.eventName === EventName.penalties
            ) {
                currentPeriod = matchEvent as HalfEvent;
                currentPeriodInfo = playingPeriodInfo[currentPeriod.index];
                const endTs =
                    currentPeriod.endTime ||
                    currentPeriod.timestamp + currentPeriod.length;

                // Handle non-playing periods (e.g., half-time, breaks)
                if (!currentPeriod.playing) {
                    const nonPlayingDurationSeconds =
                        currentPeriodInfo.actualDurationSeconds || 0;
                    // Only show non-playing periods if they have ended (endTime is set)
                    if (currentPeriod.endTime && nonPlayingDurationSeconds > 0) {
                        output.push({
                            event: matchEvent,
                            displayMinute: formatDurationWithLabel(
                                nonPlayingDurationSeconds
                            ),
                            type: DisplayEventType.nonPlayingPeriod,
                            period: currentPeriod,
                            sortingTs: matchEvent.timestamp,
                        });
                    }
                } else {
                    // Handle playing periods
                    // period start
                    output.push({
                        event: matchEvent,
                        displayMinute: generateMinuteString(
                            matchEvent,
                            currentPeriodInfo
                        ),
                        type: DisplayEventType.periodStart,
                        period: currentPeriod,
                        sortingTs: matchEvent.timestamp,
                    });

                    // period end
                    const endDisplayMinute = stoppingClock
                        ? stoppingClockPeriodEndDisplay(
                              currentPeriod,
                              sortedEvents
                          )
                        : formatPeriodEndDisplay(currentPeriodInfo);

                    output.push({
                        event: matchEvent,
                        displayMinute: endDisplayMinute,
                        type: DisplayEventType.periodEnd,
                        period: currentPeriod,
                        sortingTs: endTs,
                        score: getHalfTimeScoreForPeriod(currentPeriod.index),
                    });
                }
            } else if (currentPeriodInfo) {
                const isStoppage = matchEvent.eventName === EventName.stoppage;
                // REFSIX-819: One Minute Timers share the stoppage display gate
                const isOneMinuteTimer =
                    matchEvent.eventName === EventName.oneMinuteTimer;
                if ((isStoppage || isOneMinuteTimer) && !showStoppages) {
                    return;
                }
                // REFSIX-647: suspensions are shown to everyone, with no setting
                const isSuspension =
                    matchEvent.eventName === EventName.suspension;
                output.push({
                    event: matchEvent,
                    displayMinute: eventDisplayMinute(
                        matchEvent,
                        match,
                        sortedEvents
                    ),
                    // Stoppages, suspensions and One Minute Timers come from the timer, so they get no edit/delete popover
                    type: isStoppage
                        ? DisplayEventType.stoppage
                        : isSuspension
                        ? DisplayEventType.suspension
                        : isOneMinuteTimer
                        ? DisplayEventType.oneMinuteTimer
                        : DisplayEventType.other,
                    period: currentPeriod,
                    sortingTs: matchEvent.timestamp,
                });
            } else {
                console.log(
                    `Event happened before the match kicked off so not displaying it ${matchEvent.timestamp}, ${matchEvent.eventName}`
                );
            }
        });
        output.sort((a, b) => {
            return a.sortingTs - b.sortingTs;
        });
        if (abandoned) {
            // if the match is abandoned we don't want to show the last half and the abandoned events
            return matchAbandoned(output);
        }
        return output;
    }

    if (!displayEvents.length) {
        return null;
    }

    return (
        <>
            {displayEvents
                .slice()
                .sort((a, b) =>
                    order === "asc"
                        ? a.sortingTs - b.sortingTs
                        : b.sortingTs - a.sortingTs
                )
                .filter((displayEvent) => {
                    if (displayEvent.event.eventName === EventName.half) {
                        const halfEvent = displayEvent.event as HalfEvent;
                        // Keep playing periods and non-playing periods with special display
                        return (
                            halfEvent.playing ||
                            displayEvent.type ===
                                DisplayEventType.nonPlayingPeriod
                        );
                    }

                    return true;
                })
                .map((displayEvent, index) => {
                    return (
                        <MatchLogComponent
                            key={index}
                            displayEvent={displayEvent}
                            timezone={match.timezone}
                            matchInProgress={match.matchInProgress}
                            onEventClick={onEventClick}
                            edit={edit ? () => edit(displayEvent) : undefined}
                            delete={
                                deleteEvent
                                    ? () => deleteEvent(displayEvent)
                                    : undefined
                            }
                        />
                    );
                })}
        </>
    );
};

export default MatchEventLog;
